import { Component } from '@angular/core';
import { ChartDataset } from 'chart.js';
import { DisplayUnit } from 'src/app/panels/models/display-unit';
import { GenerateGraphDataset, GraphType, IGraphConfig, IGraphMappedDataset } from 'src/app/panels/models/graph';
import { PanelType } from 'src/app/panels/models/panel-type';
import { Color } from 'src/app/shared/services/color/colors';
import {
  GRAPH_PANEL_IMPORTS,
  GraphPanelComponent,
} from '../../../../panels/components/graph-panel/graph-panel.component';
import { PanelComponent } from '../../../../panels/components/panel.component';

@PanelComponent.register
@Component({
  templateUrl: '../../../../panels/components/graph-panel/graph-panel.component.html',
  styleUrl: '../../../../panels/components/graph-panel/graph-panel.component.scss',
  selector: 'app-electricity-graph-panel',
  imports: GRAPH_PANEL_IMPORTS,
})
export class ElectricityGraphPanelComponent extends GraphPanelComponent {
  public static override readonly type = PanelType.ElectricityGraph;
  public override unit = DisplayUnit.Kwh;

  public override config: IGraphConfig = {
    sortOrder: [
      GraphType.ElectricityPowerUsageAvg,
      GraphType.ElectricityPowerUsageMax,
      GraphType.ElectricityPowerUsageActual,
      GraphType.ElectricityPowerUsage,
      GraphType.EnergyPrice,
      GraphType.ElectricityProduced,
      GraphType.ElectricityUsage,
      GraphType.ElectricityReturned,
      GraphType.EnergyCosts,
      GraphType.EnergyEmission,
      GraphType.ElectricityGridManagementCosts,
      GraphType.ElectricityTaxCosts,
      GraphType.ElectricityVatCosts,
      GraphType.ElectricityConsumptionCosts,
    ],
    legendSortOrder: [
      GraphType.ElectricityUsage,
      GraphType.ElectricityProduced,
      GraphType.ElectricityReturned,
      GraphType.BatteryCharge,
      GraphType.BatteryDischarge,
      GraphType.BatteryPowerCharge,
      GraphType.BatteryPowerDischarge,
      GraphType.ElectricityPowerUsageMax,
      GraphType.EnergyCosts,
      GraphType.EnergyEmission,
      GraphType.EnergyPrice,
      GraphType.BatteryLevel,
      GraphType.ElectricityConsumptionCosts,
      GraphType.ElectricityGridManagementCosts,
      GraphType.ElectricityTaxCosts,
      GraphType.ElectricityVatCosts,
    ],
    getGraphTypeDataset: this.getGraphTypeDataset,
  };

  private getGraphTypeDataset(
    generateDataset: GenerateGraphDataset,
    dataset: IGraphMappedDataset,
    startTimestamps: string[],
    endTimestamps: string[],
  ): ChartDataset {
    switch (dataset.type) {
      case GraphType.ElectricityPowerUsageMax:
        return generateDataset(
          dataset,
          startTimestamps,
          endTimestamps,
          'Vermogensvraag - maximaal',
          Color.DarkBlue,
          DisplayUnit.Kw,
          'line',
          {
            hidden: true,
            stack: undefined,
          },
        );
      case GraphType.ElectricityPowerUsageAvg:
        return generateDataset(
          dataset,
          startTimestamps,
          endTimestamps,
          'Vermogensvraag',
          Color.Blue,
          DisplayUnit.Kw,
          'line',
          {
            stack: undefined,
          },
        );
      case GraphType.ElectricityPowerUsageMin:
        return generateDataset(
          dataset,
          startTimestamps,
          endTimestamps,
          'Vermogensvraag - minimaal',
          Color.LightBlue,
          DisplayUnit.Kw,
          'line',
          {
            hidden: true,
            stack: undefined,
          },
        );
      case GraphType.ElectricityPowerProduced:
        return generateDataset(
          dataset,
          startTimestamps,
          endTimestamps,
          'Opgewekt',
          Color.Green,
          DisplayUnit.Kw,
          'line',
          {
            stack: undefined,
          },
        );
      case GraphType.ElectricityPowerReturned:
        return generateDataset(
          dataset,
          startTimestamps,
          endTimestamps,
          'Teruggeleverd',
          Color.Yellow,
          DisplayUnit.Kw,
          'line',
          {
            stack: undefined,
          },
        );
      case GraphType.BatteryLevel:
        return generateDataset(
          dataset,
          startTimestamps,
          endTimestamps,
          `Batterij (${DisplayUnit.Percentage})`,
          Color.Blue,
          DisplayUnit.Percentage,
          'line',
          {
            stack: undefined,
          },
        );
      case GraphType.ElectricityPowerUsageActual:
        return generateDataset(
          dataset,
          startTimestamps,
          endTimestamps,
          'Vermogensvraag',
          Color.LightGray,
          DisplayUnit.Kw,
          'line',
          {
            stack: undefined,
          },
        );
      case GraphType.ElectricityPowerUsage:
        return generateDataset(
          dataset,
          startTimestamps,
          endTimestamps,
          'Netverbruik',
          Color.LightBlue,
          DisplayUnit.Kw,
          'bar',
          {
            stack: undefined,
          },
        );

      case GraphType.BatteryPowerCharge:
        return generateDataset(dataset, startTimestamps, endTimestamps, 'Opladen', Color.Teal, DisplayUnit.Kw, 'line', {
          stack: undefined,
        });
      case GraphType.BatteryPowerDischarge:
        return generateDataset(
          dataset,
          startTimestamps,
          endTimestamps,
          'Ontladen',
          Color.Purple,
          DisplayUnit.Kw,
          'line',
          {
            stack: undefined,
          },
        );
      case GraphType.ElectricityUsage:
        return generateDataset(dataset, startTimestamps, endTimestamps, 'Verbruikt', Color.Blue, DisplayUnit.Kwh);
      case GraphType.ElectricityReturned:
        return generateDataset(dataset, startTimestamps, endTimestamps, 'Teruggeleverd', Color.Yellow, DisplayUnit.Kwh);
      case GraphType.ElectricityProduced:
        return generateDataset(
          dataset,
          startTimestamps,
          endTimestamps,
          'Duurzaam verbruikt',
          Color.Green,
          DisplayUnit.Kwh,
        );
      case GraphType.ElectricityPerSquareMeter:
        return generateDataset(
          dataset,
          startTimestamps,
          endTimestamps,
          `Verbruik per ${DisplayUnit.M2}`,
          Color.BrightBlue,
          DisplayUnit.KwhSquareMeter,
        );
      case GraphType.EnergyPrice:
        return generateDataset(
          dataset,
          startTimestamps,
          endTimestamps,
          'Elektriciteitsprijs',
          Color.DarkGreen,
          DisplayUnit.Euro,
          'line',
          {
            stepped: 'middle',
            hidden: true,
          },
        );
      case GraphType.EnergyCosts:
        return generateDataset(dataset, startTimestamps, endTimestamps, 'Kosten', Color.Yellow, DisplayUnit.Euro);

      case GraphType.ElectricityConsumptionCosts:
        return generateDataset(
          dataset,
          startTimestamps,
          endTimestamps,
          'Verbruikskosten',
          Color.Blue,
          DisplayUnit.Euro,
        );
      case GraphType.ElectricityGridManagementCosts:
        return generateDataset(dataset, startTimestamps, endTimestamps, 'Netbeheer', Color.Green, DisplayUnit.Euro);
      case GraphType.ElectricityTaxCosts:
        return generateDataset(
          dataset,
          startTimestamps,
          endTimestamps,
          'Energiebelasting',
          Color.Yellow,
          DisplayUnit.Euro,
        );
      case GraphType.ElectricityVatCosts:
        return generateDataset(dataset, startTimestamps, endTimestamps, 'BTW', Color.Red, DisplayUnit.Euro);

      case GraphType.EnergyEmission:
        return generateDataset(dataset, startTimestamps, endTimestamps, 'Uitstoot', Color.Red, DisplayUnit.Kg);
      default:
        throw new Error(`No dataset specified for GraphType '${dataset.type}'`);
    }
  }
}
